<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { SampleCreateDocument, SampleUpdateDocument } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BModal,
  BSpinner,
} from 'bootstrap-vue-next';
import CodeValidation from '@/components/CodeValidation.vue';
import { DefaultCode } from '@/components/CodeValidation.vue';

// --- Code Validation component ref ---
const codeValidation = ref<InstanceType<typeof CodeValidation>>();

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  bucketId: number | null;
  sampleId: number | null;
  sampleName: string;
  sampleDescription: string;
  sampleFileR1: string;
  sampleFileR2: string;
  kitCode: string;
  warning: string | null;
  error?: string | null;
}>();

const sampleKitCode = computed<DefaultCode[]>(() => {
  return [
    {
      codeString: props.kitCode,
      unitsRequired: 1,
    },
  ];
});

// --- Component emits ---
const emits = defineEmits(['close', 'closeAndRefresh']);

// --- Component state ---
const sampleName = ref<string>(props.sampleName);
const sampleDescription = ref<string>(props.sampleDescription);
const sampleFileR1 = ref<string>(props.sampleFileR1);
const sampleFileR2 = ref<string>(props.sampleFileR2);

const show = computed<boolean>(() => {
  return props.show;
});

const showWarning = computed<boolean>(() => {
  return props.warning !== null;
});

// --- Mutations State ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

// --- Mutation: Create Sample ---
const {
  mutate: executeSampleCreate,
  onDone: onSampleCreateDone,
  onError: onSampleCreateError,
} = useMutation(SampleCreateDocument);

onSampleCreateDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.sampleCreate) {
    console.debug('Sample created:', res.data.sampleCreate);
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value = 'Sample creation failed: ' + res.data;
    console.error('Sample creation failed:', res.data);
  }
});

onSampleCreateError((err) => {
  console.error('Error renaming data object:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Mutation: Update Sample ---
const {
  mutate: executeSampleUpdate,
  onDone: onSampleUpdateDone,
  onError: onSampleUpdateError,
} = useMutation(SampleUpdateDocument);

onSampleUpdateDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.sampleUpdate) {
    console.debug('Sample updated:', res.data.sampleUpdate);
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value = 'Sample update failed: ' + res.data;
    console.error('Sample update failed:', res.data);
  }
});

onSampleUpdateError((err) => {
  console.error('Error updating sample:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handle component actions ---
function mutationRequested() {
  if (props.error) return;
  if (!sampleName.value) {
    mutationError.value = 'You must provide sample name';
    console.error('Sample name not provided');
    return;
  }
  if (
    codeValidation.value?.codeValidationItems.length === 0 ||
    !codeValidation.value?.codeValidationItems[0].code?.kit?.id
  ) {
    mutationError.value = 'You must provide a valid kit code';
    console.error('Kit code/id not provided');
    return;
  }

  // set progress state
  mutationInProgress.value = true;
  mutationError.value = null;

  // create vs. udpate
  if (props.sampleId) {
    console.debug('Updating sample:', props.sampleId);
    executeSampleUpdate({
      id: props.sampleId,
      kitId: codeValidation.value.codeValidationItems[0].code.kit.id,
      name: sampleName.value,
      description: sampleDescription.value,
      fileR1: sampleFileR1.value,
      fileR2: sampleFileR2.value,
    });
  } else {
    console.debug('Creating new sample:', sampleName.value);
    executeSampleCreate({
      bucketId: props.bucketId,
      kitId: codeValidation.value.codeValidationItems[0].code.kit.id,
      name: sampleName.value,
      description: sampleDescription.value,
      fileR1: sampleFileR1.value,
      fileR2: sampleFileR2.value,
    });
  }
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <div>
    <!-- MODAL: Storage Object Rename -->
    <b-modal v-model="show" @hide="handleCloseModal">
      <!-- Modal Title -->
      <template #title>
        <template v-if="props.sampleId">
          <font-awesome-icon icon="pencil" class="me-2" />
          Edit sample
        </template>
        <template v-else>
          <font-awesome-icon icon="plus" class="me-2" />
          New sample
        </template>
      </template>
      <!-- Modal Content -->
      <div>
        <!-- Sample name -->
        <BFormGroup label="Sample name" label-for="sample-name" class="mt-3">
          <BFormInput
            v-model="sampleName"
            id="sample-name"
            name="sample-name"
            placeholder="Enter a name for the sample"
            required
          ></BFormInput>
        </BFormGroup>
        <!-- Sample description -->
        <BFormGroup
          label="Sample description"
          label-for="sample-description"
          class="mt-3"
        >
          <BFormTextarea
            v-model="sampleDescription"
            id="sample-description"
            name="sample-description"
            placeholder="A brief description of the sample."
          ></BFormTextarea>
        </BFormGroup>
        <!-- Sample file R1 -->
        <BFormGroup label="R1 File" label-for="sample-file-R1" class="mt-3">
          <BFormInput
            v-model="sampleFileR1"
            label="File R1"
            placeholder="Enter the R1 file name"
            required
          ></BFormInput>
        </BFormGroup>
        <!-- Sample file R1 -->
        <BFormGroup label="R2 File" label-for="sample-file-R2" class="mt-3">
          <BFormInput
            v-model="sampleFileR2"
            label="R2 File"
            placeholder="Enter the R2 file name"
            required
          ></BFormInput>
        </BFormGroup>

        <!-- Kit Code Validation -->
        <CodeValidation
          class="mt-3"
          ref="codeValidation"
          label-text="Kit code"
          button-text="Apply"
          :default-codes="sampleKitCode"
          :require-kit="true"
          :require-explicit-counts="false"
          :required-coupon-units-count="Number(0)"
          :required-coupon-type-name="null"
          :requried-coupon-units-name="null"
          :required-license-type-name="null"
          :immediate-validation="true"
          :hide-component-message="false"
        />

        <!-- Disclaimer -->
        <b-alert
          v-if="codeValidation"
          v-model="codeValidation.success"
          variant="success"
          class="mt-3"
        >
          Please double-check the kit code before proceeding. Incorrect kit
          association may lead to down-stream analysis errors. Correct kit
          selection provides barcode specification and other important
          information which is critical for achieving accurate results.
        </b-alert>

        <b-alert :model-value="!!props.error" variant="danger" class="mt-3">
          {{ props.error }}
        </b-alert>

        <!-- Error Message -->
        <b-alert v-model="showWarning" variant="warning" class="mt-3">
          {{ props.warning }}
        </b-alert>

        <!-- Error Message -->
        <b-alert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="mutationError = null"
        >
          {{ mutationError }}
        </b-alert>
      </div>
      <!-- Modal Footer -->
      <template #footer>
        <b-button variant="secondary" @click="handleCloseModal">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="mutationRequested"
          :disabled="
            !!props.error ||
            !sampleName ||
            codeValidation?.failure ||
            mutationInProgress ||
            mutationShowSuccess
          "
        >
          <span v-if="mutationInProgress">
            <span v-if="props.sampleId">Updating Sample</span>
            <span v-else>Creating Sample</span>
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="mutationShowSuccess">
            <span v-if="props.sampleId">Sample Updated </span>
            <span v-else>Sample Created </span>
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else>
            <span v-if="props.sampleId">Update Sample</span>
            <span v-else>Create Sample</span>
          </span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
